import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLock, FaLockOpen, FaSave } from 'react-icons/fa';
import U2000BasicDialog from '../../../../../u2000Core/components/dialog/U2000BasicDialog';
import U2000YesNoDialog from '../../../../../u2000Core/components/dialog/U2000YesNoDialog';
import U2000ActionBtn from '../../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000CheckBox from '../../../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import { useU2000AuthContext } from '../../../../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import { U2000FormStateEnumState } from '../../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import useU2000AxiosAuthenticated from '../../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import { NS, U3141ROLES } from '../../../../U3141';
import {
  doGetChildLotChainData,
  luHandleAppiError,
  U3141EmptyChildLotChain,
  type U3141ChildLotChain,
} from '../../../../U3141Service';
import U3141ChildLotChainLotFields from '../../u3141ChildLotChain/U3141ChildLotChainLotFields';
import { validateConcordanceEntries } from '../../u3141ChildLotChain/U3141ConcordanceUtils';
import { useU3141UpdateChildLotChainFromGraphHandlers } from './U3141UseUpdateChildLotChainFromGraphHandlers';
import { U3141GraphLotDialogHelpPopover } from './U3141GraphLotDialogHelpPopover';
import {
  u3141CloneChildLotChainSnapshot,
  u3141IsGraphAddFormDirty,
} from '../../../../tools/u3141GraphAddFormDirty';
import {
  u3141CaptureConcordanceSnapshot,
  type U3141GraphEditConcordanceSnapshot,
} from '../../../../tools/u3141GraphEditConcordanceSnapshot';
import {
  u3141GraphLotDefaultFieldsLocked,
  u3141GraphLotLockCanApply,
  u3141GraphLotLockCloseDisabled,
} from '../../../../tools/u3141GraphLotDialogLock';

/** BL-0697 — seules les concordances mal formées bloquent l’enregistrement graphe. */
function validateEditLotForm(
  _child: U3141ChildLotChain,
  concordanceForValidation: string[],
  t: (key: string, opts?: { ns: string }) => string,
): Record<string, string> {
  const result: Record<string, string> = {};
  const concordanceValidation = validateConcordanceEntries(concordanceForValidation);
  if (!concordanceValidation.valid) {
    result['lotDetail.concordance'] = t('errConcordanceInvalid', { ns: NS });
  }
  return result;
}

function U3141GraphEditDialogSection({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      <div className="border-b border-gray-100 bg-muted/40 px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-tight text-gray-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export interface U3141EditLotFromGraphDialogProps {
  isOpen: boolean;
  onClose: () => void;
  masterLotChainId: string;
  initialChild: U3141ChildLotChain | null;
  onChildUpdated: (child: U3141ChildLotChain) => void;
  patchGraphNode: (child: U3141ChildLotChain) => void;
  refetchMasterLotChain?: () => Promise<void>;
  overlayClassName?: string;
}

export const U3141EditLotFromGraphDialog: React.FC<U3141EditLotFromGraphDialogProps> = ({
  isOpen,
  onClose,
  masterLotChainId,
  initialChild,
  onChildUpdated,
  patchGraphNode,
  refetchMasterLotChain,
  overlayClassName = 'z-110',
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const canEditLot = hasRole(U3141ROLES.EDIT);
  const axios = useU2000AxiosAuthenticated();

  const [formData, setFormData] = useState<U3141ChildLotChain>(() => ({ ...U3141EmptyChildLotChain }));
  const [isLoadingChild, setIsLoadingChild] = useState(false);
  const [openSnapshot, setOpenSnapshot] = useState<U3141ChildLotChain | null>(null);
  const [fieldsLockedByUser, setFieldsLockedByUser] = useState<boolean | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const concordanceSnapshotRef = useRef<U3141GraphEditConcordanceSnapshot>({ concordance: [] });

  const resetState = useCallback(() => {
    setFormData({ ...U3141EmptyChildLotChain });
    setOpenSnapshot(null);
    setFieldsLockedByUser(null);
    setErrors({});
    setIsSaving(false);
    setDiscardConfirmOpen(false);
    concordanceSnapshotRef.current = { concordance: [] };
    setIsLoadingChild(false);
  }, []);

  const applyChildToForm = useCallback((child: U3141ChildLotChain) => {
    const clone = u3141CloneChildLotChainSnapshot(child);
    setFormData(clone);
    setOpenSnapshot(u3141CloneChildLotChainSnapshot(clone));
    concordanceSnapshotRef.current = u3141CaptureConcordanceSnapshot(clone);
    setFieldsLockedByUser(null);
    setErrors({});
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }
    if (!initialChild?.id || !masterLotChainId) return;

    let cancelled = false;
    setIsLoadingChild(true);

    void (async () => {
      try {
        const response = await doGetChildLotChainData(axios, masterLotChainId, initialChild.id);
        const row = response.data?.result as U3141ChildLotChain | undefined;
        if (cancelled) return;
        applyChildToForm(row?.id ? row : initialChild);
      } catch (error) {
        if (cancelled) return;
        const errorResult = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError,
        );
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
        }
        applyChildToForm(initialChild);
      } finally {
        if (!cancelled) setIsLoadingChild(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialChild, initialChild?.id, masterLotChainId, axios, applyChildToForm, resetState, t, toast]);

  const editLotState = formData?.lotDetail?.lotState ?? openSnapshot?.lotDetail?.lotState;
  const graphLockAutoApply = u3141GraphLotLockCanApply(editLotState);
  const fieldsLocked =
    fieldsLockedByUser ?? u3141GraphLotDefaultFieldsLocked(editLotState, formData);
  const businessFieldsEditable = !fieldsLocked;

  const isDirty = useMemo(
    () => u3141IsGraphAddFormDirty(formData, openSnapshot),
    [formData, openSnapshot],
  );

  const formStateHandlers = useMemo((): U2000FormStateHandlers<U3141ChildLotChain> => {
    const noop = () => {};
    return {
      getData: () => formData,
      setData: setFormData,
      getFormState: () => U2000FormStateEnumState.EDIT,
      getErrors: () => errors,
      setErrors,
      setEditMode: noop,
      saveData: noop,
      getOldData: () => openSnapshot,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => 0,
      getTouched: () => ({}),
      markTouched: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isSubmitted: () => false,
      isValid: () => Object.keys(errors).length === 0,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      runAfterFormCommitNavigation: (_nav: () => void) => {},
    };
  }, [formData, errors, openSnapshot]);

  const { updateFromGraph } = useU3141UpdateChildLotChainFromGraphHandlers({
    t,
    toast,
    onChildUpdated,
    patchGraphNode,
    refetchMasterLotChain,
  });

  const toggleFieldsLock = () => {
    if (!canEditLot) return;
    if (u3141GraphLotLockCloseDisabled(fieldsLocked, formData?.lotDetail)) return;
    setFieldsLockedByUser(!fieldsLocked);
  };

  const lockCloseDisabled = u3141GraphLotLockCloseDisabled(fieldsLocked, formData?.lotDetail);

  const lockTooltip = !canEditLot
    ? t('ttlAddLotFromGraphLockNoEdit', { ns: NS })
    : lockCloseDisabled
      ? t('ttlAddLotFromGraphLockRequiredFieldsForClose', { ns: NS })
      : graphLockAutoApply
      ? fieldsLocked
        ? t('ttlAddLotFromGraphLockClosed', { ns: NS })
        : t('ttlAddLotFromGraphLockOpen', { ns: NS })
      : fieldsLocked
        ? t('ttlAddLotFromGraphLockClosed', { ns: NS })
        : t('ttlAddLotFromGraphLockManualOnly', { ns: NS });

  const lotValidatedBadge = useMemo(() => {
    if (openSnapshot?.lotDetail?.lotState !== 'validate') return null;
    return (
      <span className="inline-flex h-5 max-h-5 items-center rounded-md border border-emerald-300/80 bg-emerald-50 px-2 py-0 text-xs font-semibold leading-none text-emerald-950">
        {t('lblAddLotFromGraphValidated', { ns: NS })}
      </span>
    );
  }, [openSnapshot?.lotDetail?.lotState, t]);

  const editHelpContextHint = useMemo(() => {
    if (!openSnapshot?.lotDetail || isLoadingChild) return null;
    if (openSnapshot.lotDetail.lotState === 'validate') {
      return t('msgEditLotFromGraphHelpValidated', { ns: NS });
    }
    return t('msgEditLotFromGraphHelpEditable', { ns: NS });
  }, [isLoadingChild, openSnapshot?.lotDetail, t]);

  const graphEditLotInlineEnd = useMemo(
    () => (
      <div className="flex max-w-full flex-wrap items-center justify-end gap-2 sm:gap-3">
        <U2000CheckBox
          formStateHandlers={formStateHandlers}
          fieldName="lotDetail.partieDeLot"
          label={t('lblLotDetail.partieDeLot', { ns: NS })}
          readonly={!businessFieldsEditable}
        />
        <U2000ActionBtn
          variant="secondary"
          icon={fieldsLocked ? FaLock : FaLockOpen}
          label={t('lblAddLotFromGraphLockToggle', { ns: NS })}
          handleClick={toggleFieldsLock}
          disabled={!canEditLot || isSaving || lockCloseDisabled}
          title={lockTooltip}
        />
      </div>
    ),
    [businessFieldsEditable, canEditLot, fieldsLocked, formStateHandlers, isSaving, lockCloseDisabled, lockTooltip, t, toggleFieldsLock],
  );

  const requestClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [isDirty, isSaving, onClose]);

  const handleSave = async () => {
    const validationErrors = validateEditLotForm(formData, concordanceSnapshotRef.current.concordance, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstMessage = Object.values(validationErrors)[0] ?? t('errEditLotFromGraphValidation', { ns: NS });
      toast.error(firstMessage, t('errorTitle', { ns: NS }));
      return;
    }
    setIsSaving(true);
    const saved = await updateFromGraph({
      masterLotChainId,
      childLotChain: formData,
      concordanceSnapshot: concordanceSnapshotRef.current,
    });
    setIsSaving(false);
    if (saved) {
      onClose();
    }
  };

  const nestedOverlay = overlayClassName === 'z-110' ? 'z-120' : 'z-130';

  if (!isOpen || !initialChild?.id) {
    return null;
  }

  return (
    <>
      <U2000BasicDialog
        isOpen={isOpen}
        onClose={requestClose}
        title={t('dlgEditLotFromGraphTitle', { ns: NS })}
        width="w-[min(920px,92vw)]"
        height="max-h-[90vh]"
        overlayClassName={overlayClassName}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1 pb-4 pt-0">
            <U3141GraphEditDialogSection title={t('lblAddLotFromGraphSectionDetails', { ns: NS })}>
              {isLoadingChild ? (
                <p className="text-sm text-gray-600">{t('msgEditLotFromGraphLoading', { ns: NS })}</p>
              ) : (
                <U3141ChildLotChainLotFields
                  formStateHandlers={formStateHandlers}
                  mode="graphAdd"
                  businessFieldsEditable={businessFieldsEditable}
                  graphAddLotLabelAddon={lotValidatedBadge}
                  graphAddLotInlineEnd={graphEditLotInlineEnd}
                />
              )}
            </U3141GraphEditDialogSection>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-muted/30 px-3 py-3">
            <U3141GraphLotDialogHelpPopover
              mode="edit"
              t={t}
              contextualHint={editHelpContextHint}
              disabled={isSaving || isLoadingChild}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <U2000ActionBtn
                variant="secondary"
                label={t('btnAddLotFromGraphCancel', { ns: NS })}
                handleClick={requestClose}
                disabled={isSaving}
              />
              <U2000ActionBtn
                icon={FaSave}
                label={isSaving ? t('msgAddLotFromGraphSaving', { ns: NS }) : t('btnAddLotFromGraphSave', { ns: NS })}
                handleClick={() => void handleSave()}
                disabled={isSaving || isLoadingChild || !canEditLot}
              />
            </div>
          </div>
        </div>
      </U2000BasicDialog>

      <U2000YesNoDialog
        isOpen={discardConfirmOpen}
        onClose={(result) => {
          if (result === 'yes') {
            setDiscardConfirmOpen(false);
            onClose();
          } else {
            setDiscardConfirmOpen(false);
          }
        }}
        title={t('dlgEditLotFromGraphDiscardTitle', { ns: NS })}
        width="max-w-md"
        overlayClassName={nestedOverlay}
      >
        <p className="max-w-sm text-sm leading-relaxed text-gray-700">{t('dlgEditLotFromGraphDiscardMessage', { ns: NS })}</p>
      </U2000YesNoDialog>
    </>
  );
};
